/**
 * DemocratCharts — chart component.
 *
 * Two stacked chart instances:
 *   - Main chart: candles, volume, EMA/SMA/VWAP/BB overlays, drawings,
 *     drag handles for selection, moon-phase markers
 *   - Sub-pane (collapsible): MFI / RSI / MACD with synchronized time scale
 *
 * Sync is bidirectional via subscribeVisibleLogicalRangeChange.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  createChart,
  ColorType,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type IPriceLine,
  type CandlestickData,
  type HistogramData,
  type LineData,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import type { Candle } from "@/hooks/useCandles";
import { tsToChartTime } from "@/lib/format";
import { useThemeStore, readChartColors } from "@/store/theme";
import {
  computeFibRetracement,
  computeFibExtension,
  FIB_RETRACEMENT_LEVELS,
  FIB_EXTENSION_LEVELS,
} from "@/lib/fibonacci";

export type LineStyleName = "solid" | "dashed" | "dotted";

export interface PriceLineSpec {
  id: string;
  price: number;
  color: string;
  title: string;
  lineStyle?: LineStyleName;
  lineWidth?: 1 | 2 | 3;
}

export interface IndicatorLineSpec {
  id: string;
  data: { ts: string; value: number | null }[];
  color: string;
  lineWidth?: 1 | 2 | 3;
  lineStyle?: LineStyleName;
}

export interface SubPaneSeriesSpec {
  id: string;
  data: { ts: string; value: number | null }[];
  color: string;
  lineWidth?: 1 | 2 | 3;
  /** Optional reference levels drawn as horizontal price lines (e.g. 20/80 for MFI) */
  levels?: { price: number; color: string; title: string }[];
  /** Title shown in the corner of the sub-pane */
  title?: string;
}

export interface DrawingSpec {
  id: string;
  kind: "trend" | "horizontal" | "fib_retracement" | "fib_extension" | "cycle";
  /** trend: 2 points; cycle: 2 points (anchor + second); fib_retracement: 2; fib_extension: 3 */
  points?: { ts: string; price: number }[];
  /** horizontal: single price */
  price?: number;
  /** cycle: number of cycle lines to project */
  count?: number;
  color: string;
  lineWidth: number;
}

export interface MoonMarker {
  ts: string;
  kind: "new" | "first_quarter" | "full" | "last_quarter";
}

export interface DraggedPoint {
  drawingId: string;
  pointIndex: number;
  ts: string;
  price: number;
}

interface ChartProps {
  candles: Candle[];
  onHover?: (candle: Candle | null) => void;
  onClick?: (timeSeconds: number, price: number | null) => void;
  setupCursor?: boolean;
  cursor?: "default" | "crosshair";
  priceLines?: PriceLineSpec[];
  indicators?: IndicatorLineSpec[];
  drawings?: DrawingSpec[];
  moonMarkers?: MoonMarker[];
  selectedDrawingId?: string | null;
  onDrawingDrag?: (update: DraggedPoint) => void;
  onDrawingDragStart?: () => void;
  onDrawingDragEnd?: () => void;
  /** Series to render in a separate pane below the main chart (e.g. MFI). */
  subPaneSeries?: SubPaneSeriesSpec[];
  /** Fires on every cursor move with the time/price under the cursor. */
  onCursorMove?: (data: { ts: string | null; price: number | null }) => void;
  /** Ghost drawing rendered with dashed/light styling while a drawing tool is in progress. */
  previewDrawing?: DrawingSpec | null;
  /** Latest live tick — updates the last candle in place (close, high if exceeded, low if below). */
  liveTick?: { last_price: number; ts: string } | null;
  /** Faint centered watermark, e.g. "NIFTY · 1D". */
  watermark?: string;
}

const STYLE_MAP: Record<LineStyleName, LineStyle> = {
  solid:  LineStyle.Solid,
  dashed: LineStyle.Dashed,
  dotted: LineStyle.Dotted,
};

const MOON_MARKER_STYLE: Record<
  MoonMarker["kind"],
  { text: string; color: string; position: "aboveBar" | "belowBar" }
> = {
  new:            { text: "🌑", color: "#787b86", position: "belowBar" },
  first_quarter:  { text: "🌓", color: "#bcaaa4", position: "belowBar" },
  full:           { text: "🌕", color: "#ffd54f", position: "aboveBar" },
  last_quarter:   { text: "🌗", color: "#bcaaa4", position: "aboveBar" },
};

export function Chart({
  candles, onHover, onClick, setupCursor, cursor,
  priceLines, indicators, drawings, moonMarkers,
  selectedDrawingId, onDrawingDrag, onDrawingDragStart, onDrawingDragEnd,
  subPaneSeries, onCursorMove, previewDrawing, liveTick, watermark,
}: ChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const subPaneContainerRef = useRef<HTMLDivElement>(null);

  const chartRef = useRef<IChartApi | null>(null);
  const subPaneChartRef = useRef<IChartApi | null>(null);

  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);

  const onHoverRef = useRef(onHover);
  const onClickRef = useRef(onClick);
  const onCursorMoveRef = useRef(onCursorMove);
  useEffect(() => { onHoverRef.current = onHover; }, [onHover]);
  useEffect(() => { onClickRef.current = onClick; }, [onClick]);
  useEffect(() => { onCursorMoveRef.current = onCursorMove; }, [onCursorMove]);

  const showSubPane = (subPaneSeries?.length ?? 0) > 0;

  // ─── Init main chart ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const colors = readChartColors();

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.background },
        textColor: colors.text,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
      },
      grid: { vertLines: { color: colors.grid }, horzLines: { color: colors.grid } },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: colors.crosshair, labelBackgroundColor: colors.accent },
        horzLine: { color: colors.crosshair, labelBackgroundColor: colors.accent },
      },
      rightPriceScale: { borderColor: colors.border, scaleMargins: { top: 0.05, bottom: 0.25 } },
      timeScale: { borderColor: colors.border, timeVisible: true, secondsVisible: false },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: colors.bull, downColor: colors.bear,
      borderUpColor: colors.bull, borderDownColor: colors.bear,
      wickUpColor: colors.bull, wickDownColor: colors.bear,
    });

    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      color: colors.bull,
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeCrosshairMove((param) => {
      // Hover candle data (only when over a real data point)
      if (param.time && candleSeries) {
        const data = param.seriesData.get(candleSeries) as CandlestickData | undefined;
        const vol  = param.seriesData.get(volumeSeries!)  as HistogramData | undefined;
        if (data) {
          onHoverRef.current?.({
            ts: typeof param.time === "string" ? param.time : new Date((param.time as number) * 1000).toISOString(),
            open: data.open, high: data.high, low: data.low, close: data.close,
            volume: vol?.value ?? 0,
          });
        } else {
          onHoverRef.current?.(null);
        }
      } else {
        onHoverRef.current?.(null);
      }

      // Cursor position — fires on EVERY move including off-data, derives ts via coordinateToTime
      if (param.point && candleSeries && chart) {
        const price = candleSeries.coordinateToPrice(param.point.y);
        const t = chart.timeScale().coordinateToTime(param.point.x);
        if (price !== null && t !== null) {
          const tsec = typeof t === "string"
            ? Math.floor(new Date(t).getTime() / 1000)
            : (t as number);
          onCursorMoveRef.current?.({ ts: new Date(tsec * 1000).toISOString(), price });
        } else {
          onCursorMoveRef.current?.({ ts: null, price: null });
        }
      } else {
        onCursorMoveRef.current?.({ ts: null, price: null });
      }
    });

    chart.subscribeClick((param) => {
      if (!param.time) return;
      const t = typeof param.time === "string"
        ? Math.floor(new Date(param.time).getTime() / 1000)
        : (param.time as number);
      const point = param.point;
      let price: number | null = null;
      if (point && candleSeriesRef.current) {
        price = candleSeriesRef.current.coordinateToPrice(point.y);
      }
      onClickRef.current?.(t, price);
    });

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  // ─── Init sub-pane chart on first show, destroy on hide ────────────────────
  const subPaneSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const subPaneLevelsRef = useRef<Map<string, IPriceLine[]>>(new Map());

  useEffect(() => {
    if (!showSubPane) {
      // Tear down
      if (subPaneChartRef.current) {
        subPaneChartRef.current.remove();
        subPaneChartRef.current = null;
        subPaneSeriesRef.current.clear();
        subPaneLevelsRef.current.clear();
      }
      return;
    }
    if (!subPaneContainerRef.current || subPaneChartRef.current) return;

    const colors = readChartColors();
    const chart = createChart(subPaneContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.background },
        textColor: colors.text,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 10,
      },
      grid: { vertLines: { color: colors.grid }, horzLines: { color: colors.grid } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: colors.border },
      timeScale: { borderColor: colors.border, visible: false },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale:  { mouseWheel: true, pinch: true, axisPressedMouseMove: { time: true, price: false } },
      width: subPaneContainerRef.current.clientWidth,
      height: subPaneContainerRef.current.clientHeight,
    });
    subPaneChartRef.current = chart;

    // Bidirectional time-scale sync with the main chart
    const main = chartRef.current;
    if (main) {
      const mainTs = main.timeScale();
      const subTs = chart.timeScale();

      let syncing = false;
      const fromMain = (range: any) => {
        if (syncing || !range) return;
        syncing = true;
        subTs.setVisibleLogicalRange(range);
        syncing = false;
      };
      const fromSub = (range: any) => {
        if (syncing || !range) return;
        syncing = true;
        mainTs.setVisibleLogicalRange(range);
        syncing = false;
      };
      mainTs.subscribeVisibleLogicalRangeChange(fromMain);
      subTs.subscribeVisibleLogicalRangeChange(fromSub);

      // Initial align
      const initial = mainTs.getVisibleLogicalRange();
      if (initial) subTs.setVisibleLogicalRange(initial);

      // Stash unsubs on the chart for cleanup
      (chart as any).__unsubs = [
        () => mainTs.unsubscribeVisibleLogicalRangeChange(fromMain),
        () => subTs.unsubscribeVisibleLogicalRangeChange(fromSub),
      ];
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        chart.applyOptions({ width, height });
      }
    });
    if (subPaneContainerRef.current) resizeObserver.observe(subPaneContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      const unsubs = (chart as any).__unsubs as Array<() => void> | undefined;
      unsubs?.forEach((u) => u());
      chart.remove();
      subPaneChartRef.current = null;
      subPaneSeriesRef.current.clear();
      subPaneLevelsRef.current.clear();
    };
  }, [showSubPane]);

  // ─── Push candle data to main ──────────────────────────────────────────────
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!candleSeries || !volumeSeries) return;

    if (candles.length === 0) {
      candleSeries.setData([]);
      volumeSeries.setData([]);
      return;
    }
    // Read theme so volume bar colors track light/dark
    const colors = readChartColors();
    const bullRgba = colors.bullAlpha(0.5);
    const bearRgba = colors.bearAlpha(0.5);

    const candleData: CandlestickData[] = candles.map((c) => ({
      time: tsToChartTime(c.ts) as Time,
      open: c.open, high: c.high, low: c.low, close: c.close,
    }));
    const volumeData: HistogramData[] = candles.map((c) => ({
      time: tsToChartTime(c.ts) as Time,
      value: c.volume,
      color: c.close >= c.open ? bullRgba : bearRgba,
    }));
    candleSeries.setData(candleData);
    volumeSeries.setData(volumeData);
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  // ─── Moon markers on candle series ─────────────────────────────────────────
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;
    if (!moonMarkers || moonMarkers.length === 0) {
      candleSeries.setMarkers([]);
      return;
    }
    const markers: SeriesMarker<Time>[] = moonMarkers.map((m) => ({
      time: tsToChartTime(m.ts) as Time,
      position: MOON_MARKER_STYLE[m.kind].position,
      color: MOON_MARKER_STYLE[m.kind].color,
      shape: "circle" as const,
      text: MOON_MARKER_STYLE[m.kind].text,
    }));
    candleSeries.setMarkers(markers);
  }, [moonMarkers]);

  // ─── Setup-cursor crosshair styling ────────────────────────────────────────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const colors = readChartColors();
    chart.applyOptions({
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: setupCursor ? colors.accent : colors.crosshair,
          width: setupCursor ? 2 : 1,
          style: setupCursor ? 2 : 0,
          labelBackgroundColor: colors.accent,
        },
        horzLine: {
          color: setupCursor ? colors.accent : colors.crosshair,
          labelBackgroundColor: colors.accent,
        },
      },
    });
  }, [setupCursor]);

  // ─── Theme change: re-apply colors to both charts ──────────────────────────
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    const colors = readChartColors();
    const mainChart = chartRef.current;
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;

    if (mainChart) {
      mainChart.applyOptions({
        layout: { background: { type: ColorType.Solid, color: colors.background }, textColor: colors.text },
        grid: { vertLines: { color: colors.grid }, horzLines: { color: colors.grid } },
        rightPriceScale: { borderColor: colors.border },
        timeScale: { borderColor: colors.border },
      });
    }
    if (candleSeries) {
      candleSeries.applyOptions({
        upColor: colors.bull, downColor: colors.bear,
        borderUpColor: colors.bull, borderDownColor: colors.bear,
        wickUpColor: colors.bull, wickDownColor: colors.bear,
      });
    }
    if (volumeSeries) {
      volumeSeries.applyOptions({ color: colors.bull });
    }
    // Re-color volume bars (need new data with new alpha)
    if (candleSeries && volumeSeries && candles.length > 0) {
      const bullRgba = colors.bullAlpha(0.5);
      const bearRgba = colors.bearAlpha(0.5);
      const volumeData: HistogramData[] = candles.map((c) => ({
        time: tsToChartTime(c.ts) as Time,
        value: c.volume,
        color: c.close >= c.open ? bullRgba : bearRgba,
      }));
      volumeSeries.setData(volumeData);
    }

    const subPane = subPaneChartRef.current;
    if (subPane) {
      subPane.applyOptions({
        layout: { background: { type: ColorType.Solid, color: colors.background }, textColor: colors.text },
        grid: { vertLines: { color: colors.grid }, horzLines: { color: colors.grid } },
        rightPriceScale: { borderColor: colors.border },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // ─── Watermark (symbol · timeframe) — theme-aware, uses the grid color ─────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const colors = readChartColors();
    chart.applyOptions({
      watermark: {
        visible: !!watermark,
        text: watermark ?? "",
        color: colors.grid,
        fontSize: 26,
        horzAlign: "center",
        vertAlign: "center",
      },
    });
  }, [watermark, theme]);

  // ─── Price lines (entry/SL/TP) ─────────────────────────────────────────────
  const priceLinesRef = useRef<Map<string, IPriceLine>>(new Map());
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    const desired = new Map((priceLines ?? []).map((p) => [p.id, p]));
    const existing = priceLinesRef.current;
    for (const [id, line] of existing) {
      if (!desired.has(id)) { series.removePriceLine(line); existing.delete(id); }
    }
    for (const [id, spec] of desired) {
      const opts = {
        price: spec.price,
        color: spec.color,
        lineWidth: (spec.lineWidth ?? 2) as 1 | 2 | 3,
        lineStyle: STYLE_MAP[spec.lineStyle ?? "solid"],
        axisLabelVisible: true,
        title: spec.title,
      };
      const cur = existing.get(id);
      if (cur) cur.applyOptions(opts);
      else existing.set(id, series.createPriceLine(opts));
    }
  }, [priceLines]);

  // ─── Indicator line series ─────────────────────────────────────────────────
  const indicatorSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const desired = new Map((indicators ?? []).map((i) => [i.id, i]));
    const existing = indicatorSeriesRef.current;
    for (const [id, series] of existing) {
      if (!desired.has(id)) { chart.removeSeries(series); existing.delete(id); }
    }
    for (const [id, spec] of desired) {
      let series = existing.get(id);
      if (!series) {
        series = chart.addLineSeries({
          color: spec.color,
          lineWidth: (spec.lineWidth ?? 2) as 1 | 2 | 3,
          lineStyle: STYLE_MAP[spec.lineStyle ?? "solid"],
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: false,
        });
        existing.set(id, series);
      } else {
        series.applyOptions({
          color: spec.color,
          lineWidth: (spec.lineWidth ?? 2) as 1 | 2 | 3,
          lineStyle: STYLE_MAP[spec.lineStyle ?? "solid"],
        });
      }
      const data: LineData[] = spec.data
        .filter((p) => p.value !== null && Number.isFinite(p.value))
        .map((p) => ({ time: tsToChartTime(p.ts) as Time, value: p.value as number }));
      series.setData(data);
    }
  }, [indicators]);

  // ─── Drawings ──────────────────────────────────────────────────────────────
  // Each drawing can spawn multiple line series (fib has 9 levels, cycle has N
  // vertical lines, etc.). We key sub-series by `${id}__${sub}` and clean up.
  const drawingSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Build desired series map for all drawings
    const desired = new Map<string, {
      drawingId: string;
      color: string;
      lineWidth: number;
      lineStyle?: LineStyleName;
      lastValueVisible?: boolean;
      data: LineData[];
    }>();

    for (const spec of drawings ?? []) {
      if (spec.kind === "trend" && spec.points && spec.points.length >= 2) {
        const sorted = [...spec.points].sort((a, b) => tsToChartTime(a.ts) - tsToChartTime(b.ts));
        desired.set(`${spec.id}__0`, {
          drawingId: spec.id, color: spec.color, lineWidth: spec.lineWidth,
          data: sorted.map((p) => ({ time: tsToChartTime(p.ts) as Time, value: p.price })),
        });
      } else if (spec.kind === "horizontal" && spec.price !== undefined && candles.length >= 2) {
        desired.set(`${spec.id}__0`, {
          drawingId: spec.id, color: spec.color, lineWidth: spec.lineWidth, lastValueVisible: true,
          data: [
            { time: tsToChartTime(candles[0].ts) as Time, value: spec.price },
            { time: tsToChartTime(candles[candles.length - 1].ts) as Time, value: spec.price },
          ],
        });
      } else if (spec.kind === "fib_retracement" && spec.points && spec.points.length === 2) {
        const [a, b] = spec.points;
        const levels = computeFibRetracement(a.price, b.price);
        const tA = tsToChartTime(a.ts) as Time;
        const tB = tsToChartTime(b.ts) as Time;
        levels.forEach((lvl, i) => {
          desired.set(`${spec.id}__${i}`, {
            drawingId: spec.id, color: lvl.color, lineWidth: 1, lineStyle: lvl.ratio === 0 || lvl.ratio === 1 ? "solid" : "dashed",
            lastValueVisible: true,
            data: [
              { time: tA, value: lvl.price },
              { time: tB, value: lvl.price },
            ],
          });
        });
      } else if (spec.kind === "fib_extension" && spec.points && spec.points.length === 3) {
        const [p0, p1, p2] = spec.points;
        const levels = computeFibExtension(p0.price, p1.price, p2.price);
        const tStart = tsToChartTime(p2.ts) as Time;
        const tEnd = candles.length ? tsToChartTime(candles[candles.length - 1].ts) as Time : tStart;
        levels.forEach((lvl, i) => {
          desired.set(`${spec.id}__${i}`, {
            drawingId: spec.id, color: lvl.color, lineWidth: 1, lineStyle: "dashed", lastValueVisible: true,
            data: [
              { time: tStart, value: lvl.price },
              { time: tEnd, value: lvl.price },
            ],
          });
        });
      } else if (spec.kind === "cycle" && spec.points && spec.points.length === 2 && candles.length >= 2) {
        const [anchor, second] = spec.points;
        const tAnchor = tsToChartTime(anchor.ts);
        const tSecond = tsToChartTime(second.ts);
        const interval = tSecond - tAnchor;
        if (interval !== 0) {
          const count = spec.count ?? 8;
          // Find data range for the vertical extent
          const lowAll = Math.min(...candles.map((c) => c.low));
          const highAll = Math.max(...candles.map((c) => c.high));
          for (let i = 0; i < count; i++) {
            const t = tAnchor + interval * i;
            desired.set(`${spec.id}__${i}`, {
              drawingId: spec.id, color: spec.color, lineWidth: spec.lineWidth, lineStyle: "dashed",
              data: [
                { time: t as Time, value: lowAll },
                { time: t as Time, value: highAll },
              ],
            });
          }
        }
      }
    }

    const existing = drawingSeriesRef.current;
    for (const [key, s] of existing) {
      if (!desired.has(key)) { chart.removeSeries(s); existing.delete(key); }
    }
    for (const [key, spec] of desired) {
      let series = existing.get(key);
      if (!series) {
        series = chart.addLineSeries({
          color: spec.color,
          lineWidth: spec.lineWidth as 1 | 2 | 3,
          lineStyle: STYLE_MAP[spec.lineStyle ?? "solid"],
          priceLineVisible: false,
          lastValueVisible: spec.lastValueVisible ?? false,
          crosshairMarkerVisible: false,
        });
        existing.set(key, series);
      } else {
        series.applyOptions({
          color: spec.color,
          lineWidth: spec.lineWidth as 1 | 2 | 3,
          lineStyle: STYLE_MAP[spec.lineStyle ?? "solid"],
          lastValueVisible: spec.lastValueVisible ?? false,
        });
      }
      series.setData(spec.data);
    }
  }, [drawings, candles]);

  // ─── Preview drawing (ghost while user is collecting clicks) ───────────────
  const previewSeriesRef = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const existing = previewSeriesRef.current;
    const desired = new Map<string, { color: string; data: LineData[] }>();

    if (previewDrawing) {
      const ghostColor = previewDrawing.color || "#9598a1";
      const spec = previewDrawing;
      if (spec.kind === "trend" && spec.points && spec.points.length === 2) {
        const sorted = [...spec.points].sort((a, b) => tsToChartTime(a.ts) - tsToChartTime(b.ts));
        desired.set("__preview__0", {
          color: ghostColor,
          data: sorted.map((p) => ({ time: tsToChartTime(p.ts) as Time, value: p.price })),
        });
      } else if (spec.kind === "horizontal" && spec.price !== undefined && candles.length >= 2) {
        desired.set("__preview__0", {
          color: ghostColor,
          data: [
            { time: tsToChartTime(candles[0].ts) as Time, value: spec.price },
            { time: tsToChartTime(candles[candles.length - 1].ts) as Time, value: spec.price },
          ],
        });
      } else if (spec.kind === "fib_retracement" && spec.points && spec.points.length === 2) {
        const [a, b] = spec.points;
        const levels = computeFibRetracement(a.price, b.price);
        const tA = tsToChartTime(a.ts) as Time;
        const tB = tsToChartTime(b.ts) as Time;
        levels.forEach((lvl, i) => {
          desired.set(`__preview__${i}`, {
            color: lvl.color,
            data: [{ time: tA, value: lvl.price }, { time: tB, value: lvl.price }],
          });
        });
      } else if (spec.kind === "fib_extension" && spec.points && spec.points.length === 3) {
        const [p0, p1, p2] = spec.points;
        const levels = computeFibExtension(p0.price, p1.price, p2.price);
        const tStart = tsToChartTime(p2.ts) as Time;
        const tEnd = candles.length ? tsToChartTime(candles[candles.length - 1].ts) as Time : tStart;
        levels.forEach((lvl, i) => {
          desired.set(`__preview__${i}`, {
            color: lvl.color,
            data: [{ time: tStart, value: lvl.price }, { time: tEnd, value: lvl.price }],
          });
        });
      } else if (spec.kind === "cycle" && spec.points && spec.points.length === 2 && candles.length >= 2) {
        const [anchor, second] = spec.points;
        const tA = tsToChartTime(anchor.ts);
        const tS = tsToChartTime(second.ts);
        const interval = tS - tA;
        if (interval !== 0) {
          const lowAll = Math.min(...candles.map((c) => c.low));
          const highAll = Math.max(...candles.map((c) => c.high));
          const count = spec.count ?? 8;
          for (let i = 0; i < count; i++) {
            const t = tA + interval * i;
            desired.set(`__preview__${i}`, {
              color: ghostColor,
              data: [{ time: t as Time, value: lowAll }, { time: t as Time, value: highAll }],
            });
          }
        }
      }
    }

    for (const [key, s] of existing) {
      if (!desired.has(key)) { chart.removeSeries(s); existing.delete(key); }
    }
    for (const [key, spec] of desired) {
      let s = existing.get(key);
      if (!s) {
        s = chart.addLineSeries({
          color: spec.color,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        existing.set(key, s);
      } else {
        s.applyOptions({ color: spec.color, lineStyle: LineStyle.Dashed, lineWidth: 1 });
      }
      s.setData(spec.data);
    }
  }, [previewDrawing, candles]);

  // ─── Sub-pane series (MFI, RSI, MACD…) ─────────────────────────────────────
  useEffect(() => {
    const chart = subPaneChartRef.current;
    if (!chart) return;
    const desired = new Map((subPaneSeries ?? []).map((s) => [s.id, s]));
    const existing = subPaneSeriesRef.current;
    const existingLevels = subPaneLevelsRef.current;

    for (const [id, series] of existing) {
      if (!desired.has(id)) {
        chart.removeSeries(series);
        existing.delete(id);
        existingLevels.delete(id);
      }
    }

    for (const [id, spec] of desired) {
      let series = existing.get(id);
      if (!series) {
        series = chart.addLineSeries({
          color: spec.color,
          lineWidth: (spec.lineWidth ?? 2) as 1 | 2 | 3,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: false,
        });
        existing.set(id, series);
      } else {
        series.applyOptions({
          color: spec.color,
          lineWidth: (spec.lineWidth ?? 2) as 1 | 2 | 3,
        });
      }
      const data: LineData[] = spec.data
        .filter((p) => p.value !== null && Number.isFinite(p.value))
        .map((p) => ({ time: tsToChartTime(p.ts) as Time, value: p.value as number }));
      series.setData(data);

      // Reference levels (e.g. 20/80 for MFI/RSI)
      const oldLevels = existingLevels.get(id) ?? [];
      for (const l of oldLevels) series.removePriceLine(l);
      const newLevels: IPriceLine[] = (spec.levels ?? []).map((lvl) =>
        series!.createPriceLine({
          price: lvl.price,
          color: lvl.color,
          lineStyle: LineStyle.Dashed,
          lineWidth: 1,
          axisLabelVisible: true,
          title: lvl.title,
        })
      );
      existingLevels.set(id, newLevels);
    }
  }, [subPaneSeries]);

  // ─── Drag handles for selected drawing ────────────────────────────────────
  const [handles, setHandles] = useState<
    { drawingId: string; pointIndex: number; x: number; y: number; ts: string; price: number }[]
  >([]);

  const recomputeHandles = useCallback(() => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series || !selectedDrawingId || !drawings) { setHandles([]); return; }
    const drawing = drawings.find((d) => d.id === selectedDrawingId);
    if (!drawing) { setHandles([]); return; }

    const newHandles: typeof handles = [];

    const addHandle = (pIdx: number, ts: string, price: number) => {
      const x = chart.timeScale().timeToCoordinate(tsToChartTime(ts) as Time);
      const y = series.priceToCoordinate(price);
      if (x !== null && y !== null) {
        newHandles.push({ drawingId: drawing.id, pointIndex: pIdx, x, y, ts, price });
      }
    };

    if ((drawing.kind === "trend" || drawing.kind === "fib_retracement") && drawing.points?.length === 2) {
      drawing.points.forEach((p, i) => addHandle(i, p.ts, p.price));
    } else if (drawing.kind === "fib_extension" && drawing.points?.length === 3) {
      drawing.points.forEach((p, i) => addHandle(i, p.ts, p.price));
    } else if (drawing.kind === "cycle" && drawing.points?.length === 2) {
      drawing.points.forEach((p, i) => addHandle(i, p.ts, p.price));
    } else if (drawing.kind === "horizontal" && drawing.price !== undefined && candles.length >= 2) {
      const midIdx = Math.floor(candles.length / 2);
      addHandle(0, candles[midIdx].ts, drawing.price);
    }
    setHandles(newHandles);
  }, [selectedDrawingId, drawings, candles]);

  useEffect(() => {
    recomputeHandles();
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    ts.subscribeVisibleTimeRangeChange(recomputeHandles);
    ts.subscribeVisibleLogicalRangeChange(recomputeHandles);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(recomputeHandles);
      ts.unsubscribeVisibleLogicalRangeChange(recomputeHandles);
    };
  }, [recomputeHandles]);

  // ─── Fib golden-pocket zones — translucent 0.618–0.786 band per fib ────────
  // Rendered as positioned overlay divs (lightweight-charts v4 has no native
  // between-lines fill). Recomputed on the same pan/zoom subscriptions as the
  // drag handles so the band tracks the chart transform.
  const [fibZones, setFibZones] = useState<
    { id: string; left: number; top: number; width: number; height: number; color: string }[]
  >([]);

  const recomputeFibZones = useCallback(() => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series || !drawings || drawings.length === 0) {
      setFibZones([]);
      return;
    }
    const zones: typeof fibZones = [];
    for (const d of drawings) {
      if (d.kind !== "fib_retracement" || !d.points || d.points.length !== 2) continue;
      const [a, b] = d.points;
      const span = b.price - a.price;
      const p618 = a.price + span * 0.618;
      const p786 = a.price + span * 0.786;
      const x1 = chart.timeScale().timeToCoordinate(tsToChartTime(a.ts) as Time);
      const x2 = chart.timeScale().timeToCoordinate(tsToChartTime(b.ts) as Time);
      const y1 = series.priceToCoordinate(p618);
      const y2 = series.priceToCoordinate(p786);
      if (x1 === null || x2 === null || y1 === null || y2 === null) continue;
      zones.push({
        id: d.id,
        left: Math.min(x1, x2),
        width: Math.abs(x2 - x1),
        top: Math.min(y1, y2),
        height: Math.abs(y2 - y1),
        color: d.color,
      });
    }
    setFibZones(zones);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawings, candles]);

  useEffect(() => {
    recomputeFibZones();
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    ts.subscribeVisibleTimeRangeChange(recomputeFibZones);
    ts.subscribeVisibleLogicalRangeChange(recomputeFibZones);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(recomputeFibZones);
      ts.unsubscribeVisibleLogicalRangeChange(recomputeFibZones);
    };
  }, [recomputeFibZones]);

  const dragRef = useRef<{ drawingId: string; pointIndex: number; kind: string } | null>(null);

  const onHandleMouseDown = (e: React.MouseEvent, drawingId: string, pointIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const drawing = drawings?.find((d) => d.id === drawingId);
    if (!drawing) return;
    dragRef.current = { drawingId, pointIndex, kind: drawing.kind };
    onDrawingDragStart?.();

    const handleMove = (ev: MouseEvent) => {
      if (!dragRef.current || !containerRef.current || !chartRef.current || !candleSeriesRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, ev.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, ev.clientY - rect.top));
      const price = candleSeriesRef.current.coordinateToPrice(y);

      if (dragRef.current.kind === "horizontal") {
        if (price !== null) {
          const d = drawings?.find((d) => d.id === dragRef.current!.drawingId);
          if (d) {
            onDrawingDrag?.({
              drawingId: dragRef.current.drawingId,
              pointIndex: 0,
              ts: candles[0]?.ts ?? "",
              price,
            });
          }
        }
      } else {
        const t = chartRef.current.timeScale().coordinateToTime(x);
        if (t !== null && price !== null) {
          const tsec = typeof t === "string" ? Math.floor(new Date(t).getTime() / 1000) : (t as number);
          const ts = new Date(tsec * 1000).toISOString();
          onDrawingDrag?.({
            drawingId: dragRef.current.drawingId,
            pointIndex: dragRef.current.pointIndex,
            ts, price,
          });
        }
      }
    };
    const handleUp = () => {
      dragRef.current = null;
      onDrawingDragEnd?.();
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  // ─── Live tick — update the last candle in place ──────────────────────────
  // Lightweight-charts: series.update() with the same time as the last data
  // point will UPDATE it (no full re-render). New time appends.
  //
  // The forming bar's running high/low is tracked in a ref: computing extremes
  // against the static fetched candle each tick makes earlier intra-bar wicks
  // vanish (tick 105 then 103 -> the 105 wick collapses back to 103).
  const liveBarRef = useRef<{ time: number; high: number; low: number } | null>(null);
  useEffect(() => {
    if (!liveTick || candles.length === 0) return;
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;
    const last = candles[candles.length - 1];
    const lastTime = tsToChartTime(last.ts);
    const newClose = liveTick.last_price;

    let bar = liveBarRef.current;
    if (!bar || bar.time !== lastTime) {
      // New underlying bar (fresh fetch or boundary) — reset from the candle.
      bar = { time: lastTime, high: last.high, low: last.low };
    }
    bar.high = Math.max(bar.high, newClose);
    bar.low  = Math.min(bar.low,  newClose);
    liveBarRef.current = bar;

    candleSeries.update({
      time: lastTime as Time,
      open:  last.open,
      high:  bar.high,
      low:   bar.low,
      close: newClose,
    });
  }, [liveTick, candles]);

  return (
    <div className="flex h-full w-full flex-col">
      <div
        ref={containerRef}
        className="relative flex-1"
        style={cursor === "crosshair" || setupCursor ? { cursor: "crosshair" } : undefined}
      >
        {fibZones.map((z) => (
          <div
            key={`fz-${z.id}`}
            className="pointer-events-none absolute z-10"
            style={{
              left: z.left,
              top: z.top,
              width: z.width,
              height: z.height,
              backgroundColor: `${z.color}1f`,
              borderTop: `1px solid ${z.color}55`,
              borderBottom: `1px solid ${z.color}55`,
            }}
            title="Golden pocket (0.618–0.786)"
          />
        ))}
        {handles.map((h) => (
          <div
            key={`${h.drawingId}-${h.pointIndex}`}
            onMouseDown={(e) => onHandleMouseDown(e, h.drawingId, h.pointIndex)}
            className="absolute z-20 h-3 w-3 rounded-full border-2 border-white bg-accent shadow-elevated cursor-grab active:cursor-grabbing hover:scale-125 transition-transform"
            style={{ left: h.x - 6, top: h.y - 6, pointerEvents: "auto" }}
            title={`Drag to reposition (${h.price.toFixed(2)})`}
          />
        ))}
      </div>

      {showSubPane && (
        <div className="relative h-32 shrink-0 border-t border-border">
          <div
            className="pointer-events-none absolute left-2 top-1 z-10 text-2xs font-semibold uppercase tracking-wider text-text-muted"
          >
            {subPaneSeries?.[0]?.title ?? "Sub-pane"}
          </div>
          <div ref={subPaneContainerRef} className="h-full w-full" />
        </div>
      )}
    </div>
  );
}
